import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 10;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

/**
 * A custom element that fetches and renders recently viewed products from localStorage.
 * On intersection it reads product IDs, fetches the section via Shopify's Section Rendering
 * API using a search query (id:X OR id:Y), then morphs the result into the DOM.
 * @extends {Component}
 */
class RecentlyViewedSection extends Component {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  async #load() {
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length === 0) {
      this.classList.add('hidden');
      return;
    }

    const { sectionId, maxProducts } = this.dataset;
    if (!sectionId) return;

    const limit = parseInt(maxProducts, 10) || 4;
    const productsToShow = viewedProducts.slice(0, limit);

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productsToShow.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const response = await fetch(`${url.toString()}&section_id=${sectionId}`);
      if (!response.ok) {
        this.classList.add('hidden');
        return;
      }
      const html = await response.text();
      if (html.trim()) {
        morphSection(sectionId, html, 'hydration', { injectStylesheet: true });
      } else {
        this.classList.add('hidden');
      }
    } catch {
      this.classList.add('hidden');
    }
  }
}

if (!customElements.get('recently-viewed-section')) {
  customElements.define('recently-viewed-section', RecentlyViewedSection);
}
